import React, { useEffect } from 'react';
import { Provider, useAtom } from 'jotai';
import { Spin } from 'antd';
import moment from 'moment';
import OverviewSummary from '@src/components/dpi-overview/SummaryPanel';
import { ErrorBoundary, PageTitle, Footer } from '@components/common';
import MetricPanel from '@src/components/dpi-overview/MetricPanel';
import FlowPanel from '@src/components/dpi-overview/FlowPanel';
import FrustrationSignalPanel from '@src/components/dpi-overview/FrustrationSignalPanel';
import AlertPanel from '@src/components/dpi-overview/AlertPanel';
import NetworkPanel from '@src/components/dpi-overview/NetworkPanel';
import PercentilePanel from '@src/components/dpi-overview/PercentilePanel';
import EventsPanel from '@src/components/dpi-overview/EventsPanel';
import DashboardTabs from '@components/common/Dashboard/DashboardTabs';
import RetryContext from '@src/contexts/retryContext';
import { buildDashboardStore } from '@src/atoms/utils';
import ZenPanel from '@src/components/dpi-overview/ZenPanel';
import { appliedIntervalAtom } from '@src/components/dpi-overview/atoms';
import { useBlockingPersistence } from '@src/components/dpi-overview/hooks/useBlockingPersistence';
import { usePersistOverview } from '@src/components/dpi-overview/hooks/usePersistOverview';
import { StyledOverviewWrapper, TabWrapper } from './styled';
import { DATE_SHORTCUTS } from '@src/utils/constants';
import calcShortcutInterval from '@src/utils/calcShortcutInterval';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage';
import { useFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';
import { cleanupAllPresetData } from '@src/components/dpi-overview/drillDown';
import useKPI from '@src/hooks/useKPI';
import { useSyncOverviewKpiWithKpiList } from '@src/components/dpi-overview/hooks/useSyncOverviewKpiWithKpiList';

const defaultStore = buildDashboardStore();

const DPIOverview = () => {
  const [appliedInterval, updateAppliedInterval] = useAtom(appliedIntervalAtom);
  const frustrationSignalFeatureEnabled = useFeature(featureNames.DPI_FRUSTRATION_SIGNALS);
  const { isLoading: persistenceLoading, error: persistenceError } = useBlockingPersistence();

  useKPI();
  useSyncOverviewKpiWithKpiList();

  // Call usePersistOverview once at the top level to handle all persistence
  usePersistOverview();

  // Clean up stale preset data and initialize interval on mount
  useEffect(() => {
    // Clean up any stale preset data from localStorage
    cleanupAllPresetData();

    // Initialize interval to default 24 hours
    const interval: any = calcShortcutInterval(DATE_SHORTCUTS.past24Hours);
    updateAppliedInterval({
      from: interval[0].format(),
      to: interval[1].format(),
      shortcut: DATE_SHORTCUTS.past24Hours,
      granularity: 'PT1H',
      dashboardType: 'overview',
    });
    // Only run once on mount - resets to last 24 hours on every page refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize the retry context value to prevent unnecessary re-renders
  const retryContextValue = React.useMemo(() => {
    if (appliedInterval.from && appliedInterval.to) {
      return {
        interval: { from: moment(appliedInterval.from), to: moment(appliedInterval.to) },
      };
    }

    // Fallback to 24 hours default if interval not set yet (before useEffect runs)
    const defaultInterval: any = calcShortcutInterval(DATE_SHORTCUTS.past24Hours);
    return {
      interval: { from: defaultInterval[0], to: defaultInterval[1] },
    };
  }, [appliedInterval]);

  // Show loading spinner while persistence data is being loaded
  if (persistenceLoading) {
    return (
      <StyledOverviewWrapper>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Spin size="large" />
          <div>Loading dashboard configuration...</div>
        </div>
      </StyledOverviewWrapper>
    );
  }

  // Show error state if persistence failed
  if (persistenceError) {
    console.error('Failed to load dashboard configuration.');
  }

  return (
    <RetryContext.Provider value={retryContextValue}>
      <ErrorBoundary>
        <StyledOverviewWrapper>
          <TabWrapper>
            <DashboardTabs />
          </TabWrapper>
          <PageTitle text={`Overview`} />
          <OverviewSummary />
          <div>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
              <MetricPanel />
              <FlowPanel />
              <EventsPanel />
              {frustrationSignalFeatureEnabled && <FrustrationSignalPanel />}
              {/**<PercentilePanel />*/}
              <NetworkPanel />
              <AlertPanel />
              <ZenPanel />
            </div>
            <Footer />
          </div>
          <DefaultLandingPrompt />
        </StyledOverviewWrapper>
      </ErrorBoundary>
    </RetryContext.Provider>
  );
};

const WrappedDPIOverview = (props) => {
  const { store = defaultStore }: any = props;

  return (
    <Provider store={store}>
      <DPIOverview {...props} />
    </Provider>
  );
};

export default WrappedDPIOverview;
